/**
 * Utility functions for managing Misc_Signoff.json in SharePoint juggernaut folder.
 * Structure: { "signoff": [ { workspace, card, type, name, metadata }, ... ] }
 */

export type MiscSignoffType = "prep_so" | "rev_so" | "conc_so" | "eqr_so" | "partner_so";

export interface MiscSignoffEntry {
  workspace: string;
  card: string;
  type: MiscSignoffType;
  name: string;
  metadata: string;
}

export interface MiscSignoffData {
  signoff: MiscSignoffEntry[];
}

const SIGNOFF_TYPES: MiscSignoffType[] = ["prep_so", "rev_so", "conc_so", "eqr_so", "partner_so"];

function isMiscSignoffEntry(x: unknown): x is MiscSignoffEntry {
  return (
    typeof x === "object" &&
    x !== null &&
    "workspace" in x &&
    "card" in x &&
    "type" in x &&
    "name" in x &&
    SIGNOFF_TYPES.includes((x as MiscSignoffEntry).type) &&
    typeof (x as MiscSignoffEntry).name === "string"
  );
}

/**
 * Load Misc_Signoff.json from SharePoint juggernaut folder
 */
export async function loadMiscSignoffs(): Promise<MiscSignoffData> {
  try {
    const result = await (window as any).sharePointAPI?.downloadClientJson?.("Misc_Signoff.json");
    if (result?.success && result?.data) {
      const data = result.data;
      const signoff = Array.isArray(data.signoff) ? data.signoff.filter(isMiscSignoffEntry) : [];
      return { signoff };
    }
    return { signoff: [] };
  } catch (error) {
    console.warn("Failed to load Misc_Signoff.json, starting from empty", error);
    return { signoff: [] };
  }
}

/**
 * Save Misc_Signoff.json to SharePoint juggernaut folder
 */
export async function saveMiscSignoffs(data: MiscSignoffData): Promise<{ success: boolean; error?: string }> {
  try {
    const jsonString = JSON.stringify(data, null, 2);
    const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

    const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
      fileContent: base64Content,
      fileName: "Misc_Signoff.json",
      referenceValue: "Misc Signoff",
      folderName: "juggernaut",
      sectionList: ["MiscSignoff"],
    });

    if (!uploadResult || !uploadResult.success) {
      return {
        success: false,
        error: uploadResult?.error || "Failed to save Misc_Signoff.json",
      };
    }

    return { success: true };
  } catch (error) {
    console.error("Failed to save Misc_Signoff.json", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
}

/**
 * Get sign-off entries for a specific workspace and card from the flat signoff array
 */
export function getSignoffEntriesForCard(
  workspace: string,
  card: string,
  allEntries: MiscSignoffEntry[]
): MiscSignoffEntry[] {
  return allEntries.filter((e) => e.workspace === workspace && e.card === card);
}

/**
 * Append sign-off entries for one or more cards in a workspace.
 */
export async function addMiscSignoffs(
  workspaceName: string,
  cardNames: string[],
  signoffType: MiscSignoffType,
  userName: string
): Promise<{ success: boolean; error?: string }> {
  try {
    if (cardNames.length === 0) {
      return { success: false, error: "No cards selected" };
    }
    const data = await loadMiscSignoffs();
    const entry: MiscSignoffEntry = {
      workspace: workspaceName,
      card: "",
      type: signoffType,
      name: userName,
      metadata: new Date().toISOString(),
    };
    for (const cardName of cardNames) {
      data.signoff.push({ ...entry, card: cardName });
    }
    return await saveMiscSignoffs(data);
  } catch (error) {
    console.error("Failed to add misc sign-offs", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
}
